import React, { useState, useEffect } from "react";
import logo from "../../assets/images/corlmi_1.svg";
import mark from "../../assets/images/corlmi-mark.jpg";
import AdSlot from "../AdSlot";
import DownloadAppButton from "./DownloadAppButton";
import NotificationPopup from "../NotificationPopup";

interface MakeCallProps {
  changeState: (state: string, error?: string) => void;
  changeNumber: (number: string) => void;
  number: string | undefined;
  error: string | undefined;
}

const MakeCall: React.FC<MakeCallProps> = ({
  changeState,
  changeNumber,
  number,
  error,
}) => {
  const [callState, setcallState] = useState<string>("initial");
  const [corlmiNumber, setcorlmiNumber] = useState<string | undefined>();

  useEffect(() => {
    if (number !== undefined) {
      setcorlmiNumber(number);
      setcallState("enterNumber");
    }
  }, [number]);

  const handleClick = () => {
    if (callState === "initial") {
      setcallState("enterNumber");
    } else {
      changeState("call");
    }
  };

  const handleChange = (event: { target: { value: string } }) => {
    changeNumber(event.target.value);
  };

  const numberDiv = () => (
    <div className="w-full max-w-[280px] h-[74px] flex-col justify-start items-start gap-2 inline-flex">
      <div className="self-stretch h-[74px] flex-col justify-start items-start gap-1 flex">
        <div className="text-[#344054] text-sm font-medium font-['Inter'] leading-tight">
          Call ID
        </div>
        <input
          className="self-stretch rounded-md border border-[#d0d4dd] grow shrink basis-0 h-5 justify-start items-center px-4 py-[15px] gap-0.5 flex text-[#98a1b2] text-sm font-normal font-['Inter'] leading-tight bg-white"
          placeholder="Enter call ID"
          type="text"
          value={corlmiNumber ?? ""}
          onChange={handleChange}
        />
      </div>
    </div>
  );

  const welcomeDiv = () => (
    <div className="text-[#1d2639] text-2xl font-semibold font-['Inter'] leading-[28.80px]">
      Ready to get started?
    </div>
  );

  const errorDiv = () =>
    error !== undefined ? (
      <div className="text-[#e35157] text-sm font-medium font-['Inter'] leading-tight">
        {error}
      </div>
    ) : null;

  const bannerSizeClass =
    "w-full max-w-[320px] min-h-[100px] sm:max-w-[468px] sm:min-h-[100px] md:max-w-[728px] md:min-h-[90px] lg:max-w-[970px] lg:min-h-[90px]";

  return (
    <div className="bg-white min-h-screen flex flex-col">
      <NotificationPopup />
      {/* Top ad zone */}
      <div className="w-full px-3 pt-3 sm:px-4 sm:pt-4 flex justify-center">
        <AdSlot 
          slot="top" 
          format="horizontal"
          className={bannerSizeClass} 
          dataAdSlot="9615917594"
        />
      </div>

      {/* Middle content zone — holds watermark, side rails, and main column */}
      <div className="relative flex-1 w-full flex flex-col items-center justify-start py-8 sm:py-10 md:py-12">
        {/* Background watermark — scoped to middle zone only */}
        <img
          src={mark}
          alt=""
          aria-hidden="true"
          className="pointer-events-none select-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[320px] h-[320px] sm:w-[460px] sm:h-[460px] md:w-[540px] md:h-[540px] object-contain opacity-20 z-0"
        />

        {/* Left side ad rail (tablet+) — anchored to middle zone */}
        <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-4 lg:left-8 z-10">
          <AdSlot 
            slot="left" 
            format="vertical"
            className="w-[160px] lg:w-[300px] h-[600px]" 
            dataAdSlot="8949928527"
          />
        </div>

        {/* Right side ad rail (tablet+) — anchored to middle zone */}
        <div className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-4 lg:right-8 z-10">
          <AdSlot 
            slot="right" 
            format="vertical"
            className="w-[160px] lg:w-[300px] h-[600px]" 
            dataAdSlot="3450189836"
          />
        </div>

        {/* Main column */}
        <div className="relative z-10 flex flex-col justify-center items-center w-full max-w-md px-6">
          <div className="w-full max-w-[280px]">
            <img src={logo} alt="logo" className="w-full h-auto object-contain" />
          </div>
          <div className="pt-16 flex-col justify-start items-center gap-[40px] inline-flex w-full">
            {callState === "initial" ? welcomeDiv() : numberDiv()}
            <div className="flex-col justify-start items-center gap-3 flex w-full max-w-[280px]">
              <button
                className="h-14 w-full px-6 py-4 bg-[#e35157] hover:bg-[#cf464c] transition-colors rounded-md text-white text-base font-semibold font-['Inter'] leading-normal"
                onClick={handleClick}
              >
                Make Call
              </button>
              <DownloadAppButton />
              <div className="flex-col justify-start items-center gap-4 flex">
                {errorDiv()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom ad zone */}
      <div className="w-full px-3 sm:px-4 pt-6 sm:pt-8 pb-4 sm:pb-6 flex justify-center">
        <AdSlot 
          slot="bottom" 
          format="horizontal"
          className={bannerSizeClass} 
          dataAdSlot="8399264184"
        />
      </div>

      {/* Footer with legal links */}
      <footer className="w-full border-t border-[#e4e7ec] bg-[#f9fafb] px-4 py-5 sm:py-6 flex flex-col items-center gap-1.5">
        <div className="flex items-center gap-3 text-sm font-semibold text-[#344054] font-['Inter']">
          <a
            href="https://www.corlmi.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#e35157] hover:underline transition-colors"
          >
            Privacy Policy
          </a>
          <span aria-hidden="true" className="text-[#98a1b2]">·</span>
          <a
            href="https://www.corlmi.com/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[#e35157] hover:underline transition-colors"
          >
            Terms of Service
          </a>
        </div>
        <div className="text-xs text-[#98a1b2] font-['Inter']">
          © {new Date().getFullYear()} Corlmi · All rights reserved
        </div>
      </footer>
    </div>
  );
};

export default MakeCall;
